/**
 * SequencerView Component
 * Full-screen view for building radio clock sequences with playlist slots
 * Mirrors SequencerOverlay functionality but as a standalone view, not a modal
 */

import { useState } from 'react';
import { ArrowLeft, Plus, Radio, Loader2, X } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { useSequencerStore } from '@/stores/sequencer';
import { useWorkbenchStore } from '@/stores/workbench';
import { useUIStore } from '@/stores/ui';
import { useGenerateSequence, usePreviewSequence } from '@/api/hooks/useSequencer';
import { playlistKeys } from '@/api/hooks/usePlaylists';
import { SequencerSlot } from '../workbench/SequencerSlot';
import { Button } from '../shared/Button';
import { createLogger } from '@/lib/logger';

const log = createLogger({ namespace: 'SequencerView' });

export function SequencerView() {
  const {
    slots,
    artistSeparation,
    iterations,
    stopMode,
    addSlot,
    removeSlot,
    addStopset,
    setArtistSeparation,
    setIterations,
    setStopMode,
    updateSlotMode,
    updateSlotRepeatCount,
    reset,
  } = useSequencerStore();

  const setActivePlaylist = useWorkbenchStore((state) => state.setActivePlaylist);
  const setTracks = useWorkbenchStore((state) => state.setTracks);
  const { setMode } = useUIStore();
  const queryClient = useQueryClient();

  // Playlist name and description state
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [description, setDescription] = useState('');

  // API mutations
  const generateMutation = useGenerateSequence();
  const previewMutation = usePreviewSequence();

  // Convert slots to API format (snake_case for backend)
  // Filter out empty crate slots (stopsets are always included)
  const buildApiRequest = () => {
    const validSlots = slots.filter(
      (slot) => slot.type === 'stopset' || (slot.type === 'crate' && slot.playlist !== null)
    );

    return {
      name: newPlaylistName.trim(),
      description: description.trim() || undefined,
      slots: validSlots.map((slot) => ({
        type: slot.type,
        playlist_id: slot.playlist?.id ?? null,
        mode: slot.mode,
        repeat_count: slot.repeatCount,
      })),
      artist_separation: artistSeparation,
      iterations,
      stop_mode: stopMode,
    };
  };

  // Handle generate button
  const handleGenerate = async () => {
    // Validate: at least one slot with a playlist
    const hasValidSlots = slots.some(
      (slot) => slot.type === 'crate' && slot.playlist !== null
    );

    if (!hasValidSlots) {
      // TODO: Show toast notification
      log.warn('At least one slot must have a playlist');
      return;
    }

    // Validate: playlist name required
    if (!newPlaylistName.trim()) {
      log.warn('Playlist name is required');
      return;
    }

    try {
      const request = buildApiRequest();
      log.debug('Generating sequence:', request);

      // Call API mutation
      const result = await generateMutation.mutateAsync(request);

      // Handle result - always create mode
      if (result.mode === 'create' && result.playlist) {
        // Refresh library and open new playlist in workbench
        // 1. Invalidate playlist list to refresh sidebar
        await queryClient.invalidateQueries({ queryKey: playlistKeys.all });

        // 2. Open the new playlist in workbench
        setActivePlaylist(result.playlist);
        setTracks(result.playlist.tracks.items.map((t) => t.track));

        log.debug(`Created and opened new playlist: "${result.playlist.name}" (${result.totalTracks} tracks)`);
      }

      // Success: navigate back to workbench (keep configuration for reuse)
      setMode('workbench');
    } catch (error) {
      log.error('Sequence generation failed:', error);
      // TODO: Show error toast
    }
  };

  // Handle preview button
  const handlePreview = async () => {
    try {
      const request = buildApiRequest();
      const result = await previewMutation.mutateAsync(request);

      // TODO: Show preview results in a toast or modal
      log.debug('Preview result:', result);
      log.debug(`Estimated ${result.estimatedTracks} tracks, ${Math.round(result.estimatedDuration / 60000)} minutes`);
      if (result.warnings.length > 0) {
        log.warn('Warnings:', result.warnings);
      }
    } catch (error) {
      log.error('Preview failed:', error);
      // TODO: Show error toast
    }
  };

  // Handle back to workbench
  const handleBackToWorkbench = () => {
    setMode('workbench');
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header - with back button instead of close */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackToWorkbench}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            aria-label="Back to Workbench"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <div className="p-2.5 rounded-lg bg-orange-500/10">
            <Radio className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-zinc-100">
              Radio Clock Sequencer
            </h2>
            <p className="text-sm text-zinc-500 mt-0.5 flex items-center gap-2">
              Build your sequence by dropping playlists into slots
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                Auto-saved
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 flex flex-col max-w-5xl mx-auto w-full p-6 min-h-0 overflow-hidden">
        {/* Playlist Name and Description Inputs */}
        <div className="mb-4 flex-shrink-0">
          <h3 className="text-sm font-medium text-zinc-400 mb-3">New Playlist</h3>
          <input
            type="text"
            placeholder="Enter playlist name..."
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            className="w-full px-4 py-2.5 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
          />
          <input
            type="text"
            placeholder="Description (optional)..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-4 py-2.5 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all mt-2"
          />
        </div>

        {/* Slots Section - scrollable area with fixed controls */}
        <div className="flex-1 flex flex-col min-h-0 mb-4">
          {/* Scrollable Slots Area */}
          <div className="flex-1 overflow-y-auto pr-2 -mr-2 mb-4">
            <SortableContext
              items={slots.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {slots.map((slot, idx) => (
                  <SequencerSlot
                    key={slot.id}
                    slot={slot}
                    position={idx + 1}
                    onRemove={() => removeSlot(slot.id)}
                    onModeChange={(mode) => updateSlotMode(slot.id, mode)}
                    onRepeatCountChange={(count) => updateSlotRepeatCount(slot.id, count)}
                  />
                ))}

                {slots.length === 0 && (
                  <div className="py-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-zinc-800 mb-4">
                      <Radio className="w-8 h-8 text-zinc-600" />
                    </div>
                    <p className="text-zinc-500 mb-2">No slots yet</p>
                    <p className="text-sm text-zinc-600">
                      Add slots to start building your sequence
                    </p>
                  </div>
                )}
              </div>
            </SortableContext>
          </div>

          {/* Add Slot Controls - always visible */}
          <div className="flex gap-2">
            <button
              onClick={addSlot}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 hover:border-emerald-500/50 text-emerald-400 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Slot
            </button>
            <button
              onClick={addStopset}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 hover:border-amber-500/50 text-amber-400 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Break
            </button>
            <button
              onClick={reset}
              className="px-4 py-2.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 text-red-400 text-sm font-medium transition-colors"
              title="Clear all slots and reset configuration"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Configuration Section - Compact horizontal layout */}
        <div className="flex items-center gap-4 p-3 rounded-xl bg-zinc-900/90 border border-zinc-800 mb-4 flex-shrink-0">
          {/* Artist Separation - Stepper */}
          <div className="flex items-center gap-2" title="Minimum tracks between same artist">
            <label className="text-xs text-zinc-500 whitespace-nowrap">Artist Sep</label>
            <div className="flex items-center">
              <button
                onClick={() => setArtistSeparation(Math.max(1, artistSeparation - 1))}
                className="w-6 h-6 rounded-l bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-r-0"
              >
                −
              </button>
              <span className="w-8 h-6 flex items-center justify-center text-xs font-mono text-emerald-400 bg-zinc-800/50 border-y border-zinc-700">
                {artistSeparation}
              </span>
              <button
                onClick={() => setArtistSeparation(Math.min(10, artistSeparation + 1))}
                className="w-6 h-6 rounded-r bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-l-0"
              >
                +
              </button>
            </div>
          </div>

          <div className="h-6 w-px bg-zinc-700/50" />

          {/* Iterations - Stepper */}
          <div className="flex items-center gap-2" title="Number of clock cycles">
            <label className="text-xs text-zinc-500">Cycles</label>
            <div className="flex items-center">
              <button
                onClick={() => setIterations(Math.max(1, iterations - 1))}
                className="w-6 h-6 rounded-l bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-r-0"
              >
                −
              </button>
              <span className="w-8 h-6 flex items-center justify-center text-xs font-mono text-orange-400 bg-zinc-800/50 border-y border-zinc-700">
                {iterations}
              </span>
              <button
                onClick={() => setIterations(Math.min(99, iterations + 1))}
                className="w-6 h-6 rounded-r bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-l-0"
              >
                +
              </button>
            </div>
          </div>

          <div className="h-6 w-px bg-zinc-700/50" />

          {/* Stop Mode - Segmented control */}
          <div className="flex items-center gap-2" title="When to stop generating">
            <label className="text-xs text-zinc-500">Stop</label>
            <div className="flex rounded-md overflow-hidden border border-zinc-700">
              <button
                onClick={() => setStopMode('shortest')}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                  stopMode === 'shortest'
                    ? 'bg-orange-500/20 text-orange-300'
                    : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
                }`}
              >
                First Empty
              </button>
              <button
                onClick={() => setStopMode('all')}
                className={`px-2.5 py-1 text-xs font-medium transition-colors border-l border-zinc-700 ${
                  stopMode === 'all'
                    ? 'bg-orange-500/20 text-orange-300'
                    : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
                }`}
              >
                All Empty
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-900 border border-zinc-800 flex-shrink-0">
          <p className="text-xs text-zinc-500">
            {slots.length === 0
              ? 'Add slots to continue'
              : !slots.some((s) => s.type === 'crate' && s.playlist)
                ? 'Drop playlists into slots to continue'
                : !newPlaylistName.trim()
                  ? 'Enter a playlist name to continue'
                  : `Ready to create playlist with ${slots.filter((s) => s.type === 'crate' && s.playlist).length} slot${slots.filter((s) => s.type === 'crate' && s.playlist).length > 1 ? 's' : ''}`}
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" size="md" onClick={handleBackToWorkbench}>
              Cancel
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handlePreview}
              disabled={slots.length === 0 || previewMutation.isPending}
              isLoading={previewMutation.isPending}
            >
              {previewMutation.isPending ? 'Previewing...' : 'Preview'}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleGenerate}
              disabled={
                generateMutation.isPending ||
                !slots.some((s) => s.type === 'crate' && s.playlist) ||
                !newPlaylistName.trim()
              }
              isLoading={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Playlist...
                </>
              ) : (
                'Create Playlist'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
